import { Icon, Avatar, Divider, ListItemButton, Drawer, List, ListItemText, ListItemIcon, useTheme, useMediaQuery } from '@mui/material';

import MDBox from '../../../components/MDBox';
import React, { useState } from 'react';

import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import cimbeoLogo from '../../../assets/images/small-logos/cimbeo_logo.png'

import MDTypography from '../../../components/MDTypography';
import MDButton from '../../../components/MDButton';
import AddArtifactPopup from '../../../components/AddArtifactPopup';
import WeightingDialog from '../../../components/WeightingDialog';
import { useCimbeo } from '../../../context';

function AnalyticsNavbar() {
    const [openPopup, setOpenPopup] = useState(false);
    const [openWeightingDialog, setOpenWeightingDialog] = useState(false);
    const navigate = useNavigate();

    const theme = useTheme();
    const isXl = useMediaQuery(theme.breakpoints.up('xl'));

    if (!isXl) return null;

    const handleReRunResults = () => {
        // Do a full page refresh while preserving the context
        window.location.reload();
    };

    return (
        <Drawer 
            sx={{
                width: 240,
                flexShrink: 0,
                justifyContent: "center",
                alignContent: "center",
                display: "flex",
                boxSizing: 'border-box',
                flexDirection: "column",
                '& .MuiListItemText-primary': {
                    fontSize: 14,  
                    color: "#000000"
                },
                '& .MuiListItemButton-root': {
                    display: "flex",
                    flexDirection: "coulmn",
                    alignItems:"center",
                },
                '& .Mui-selected': {
                    '&:hover': {
                        backgroundColor: "#b4914b !important"
                    },
                    backgroundColor: "#c2a66e !important",    
                    '& .MuiListItemText-primary': {
                        color: "#FFFFFF"
                    },
                },
                '& .MuiDrawer-paper': {
                    backgroundColor: ({ palette: { secondary } }) => secondary.main,
                    '&::-webkit-scrollbar': {
                        display: 'none'
                    },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                }
        }}
            classes={{
                paper: {
                    width: 240,
                    alignItems:"center",
                    justifyContent: "center"
                }
            }}
            variant="permanent" anchor="left">
            <MDButton color="slate_gray" onClick={() => navigate("/home")} sx={{p: 2, mb: 1.5, mt: 1.5}}>
                <MDBox sx={{ display: 'flex', alignItems: 'center', pr: 1, pt: 2}}>
                    <Avatar sx={{width: 44, height: 44, marginRight: 2}}
                        alt=""
                        src={cimbeoLogo}/>
                    <MDTypography variant="h2">CIMBEO</MDTypography>
                </MDBox>
            </MDButton>
            <Divider sx={{margin: 2}}/>
            <MDBox ml={1}>
                <MDTypography variant="h6">Project Actions</MDTypography>
            </MDBox>
            <MDBox sx={{mx: 2, mt: 1}}>
                <MDButton
                    variant="gradient"
                    color="navy"
                    sx={{width: "100%"}}
                    onClick={() => setOpenWeightingDialog(true)}
                >
                    Adjust Weighting Values
                </MDButton>
            </MDBox>
            <MDBox sx={{mx: 2, my: 1}}>
                <MDButton
                    variant="gradient"
                    color="navy"
                    sx={{width: "100%"}}
                    onClick={handleReRunResults}
                >
                    Re-Run Results
                </MDButton>
            </MDBox>
            <MDBox sx={{mx: 2, mb: 1}}>
                <MDButton
                    onClick={() => setOpenPopup(true)}
                    variant="gradient"
                    color="navy"
                    sx={{width: "100%"}}
                >
                    Add Artifacts
                </MDButton>
            </MDBox>
            <Divider sx={{margin: 1}}/>
            <MDBox ml={1}>
                <MDTypography variant="h6">Pages</MDTypography>
            </MDBox>
            <List>
                <NavLink to="/asset-explorer">
                    {({isActive}) => (<ListItemButton selected={isActive}>
                        <ListItemIcon>
                            <Icon>radio_button_unchecked</Icon>
                        </ListItemIcon> 
                        <ListItemText primary="Asset Explorer"/>
                    </ListItemButton>)}
                </NavLink>
                <NavLink to="/asset-comparison">
                    {({isActive}) => (<ListItemButton selected={isActive}>
                        <ListItemIcon>
                            <Icon>radio_button_unchecked</Icon>
                        </ListItemIcon>
                        <ListItemText primary="Asset Comparison"/>
                    </ListItemButton>)}
                </NavLink>
                <NavLink to="/gaps">
                    {({isActive}) => (<ListItemButton selected={isActive}>
                        <ListItemIcon>
                            <Icon>radio_button_unchecked</Icon>
                        </ListItemIcon>
                        <ListItemText primary="Gaps"/>
                    </ListItemButton>)}
                </NavLink>
                <NavLink to="/relationship-diagrams">
                    {({isActive}) => (<ListItemButton selected={isActive}>
                        <ListItemIcon>
                            <Icon>radio_button_unchecked</Icon>
                        </ListItemIcon> 
                        <ListItemText primary="Relationship Diagrams"/>
                    </ListItemButton>)}
                </NavLink>
                <NavLink to="/extracted-objects">
                    {({isActive}) => (<ListItemButton selected={isActive}>
                        <ListItemIcon>
                            <Icon>radio_button_unchecked</Icon>
                        </ListItemIcon> 
                        <ListItemText primary="Extracted Objects"/>
                    </ListItemButton>)}
                </NavLink>
                <NavLink to="/report-generator">
                    {({isActive}) => (<ListItemButton selected={isActive}>
                        <ListItemIcon>
                            <Icon>radio_button_unchecked</Icon>
                        </ListItemIcon> 
                        <ListItemText primary="Report Generator"/>
                    </ListItemButton>)}
                </NavLink>
                <ListItemButton 
                    disabled
                    sx={{ 
                        opacity: 0.5,
                        '&.Mui-disabled': {
                            opacity: 0.5
                        }
                    }}
                >
                    <ListItemIcon>
                        <Icon sx={{ opacity: 0.5 }}>radio_button_unchecked</Icon>
                    </ListItemIcon> 
                    <ListItemText primary="RFP Creator"/>
                </ListItemButton>
                <ListItemButton 
                    disabled
                    sx={{ 
                        opacity: 0.5,
                        '&.Mui-disabled': {
                            opacity: 0.5
                        }
                    }}
                >
                    <ListItemIcon>
                        <Icon sx={{ opacity: 0.5 }}>radio_button_unchecked</Icon>
                    </ListItemIcon> 
                    <ListItemText primary="Run History"/>
                </ListItemButton>
                <NavLink to="/project-settings">
                    {({isActive}) => (<ListItemButton selected={isActive}>
                        <ListItemIcon>
                            <Icon>radio_button_unchecked</Icon>
                        </ListItemIcon> 
                        <ListItemText primary="Project Settings"/>
                    </ListItemButton>)}
                </NavLink>
            </List>
            <Divider sx={{margin: 1}}/>
            <List>
                <NavLink to="/account-management">
                    {({isActive}) => (<ListItemButton selected={isActive}>
                        <ListItemIcon>
                            <Icon>radio_button_unchecked</Icon>
                        </ListItemIcon> 
                        <ListItemText primary="My Account"/>
                    </ListItemButton>)}
                </NavLink>
                <NavLink to="/admin">
                    {({isActive}) => (<ListItemButton selected={isActive}>
                        <ListItemIcon>
                            <Icon>radio_button_unchecked</Icon>
                        </ListItemIcon> 
                        <ListItemText primary="Admin"/>
                    </ListItemButton>)}
                </NavLink>
                <NavLink to="/artifact-ingestion">
                    {({isActive}) => (<ListItemButton selected={isActive}>
                        <ListItemIcon>
                            <Icon>radio_button_unchecked</Icon>
                        </ListItemIcon> 
                        <ListItemText primary="Artifact Ingestion"/>
                    </ListItemButton>)}
                </NavLink>
                <NavLink to="/projects">
                {({isActive}) => (
                    <ListItemButton selected={isActive}>
                        <ListItemIcon>
                            <Icon>radio_button_unchecked</Icon>
                        </ListItemIcon> 
                        <ListItemText primary="Projects"/>
                    </ListItemButton>
                )}
                </NavLink>
            </List>
            <AddArtifactPopup
                open={openPopup}
                onClose={() => setOpenPopup(false)}
            />
            <WeightingDialog
                open={openWeightingDialog}
                onClose={() => setOpenWeightingDialog(false)}
            />
        </Drawer>
    )
}

export default AnalyticsNavbar;